import { macroCondition, isTesting } from '@embroider/macros';
import { registerDestructor } from '@ember/destroyable';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import Feed from './feed';
export default class BroadcastFeed extends Feed {
  kind = 'broadcast'

  @tracked nowPlayingRecord;

  constructor(nowPlayingObject, options = {}) {
    super(nowPlayingObject, options);
    this.nowPlayingRecord = nowPlayingObject;
    this.startTask.perform();
    registerDestructor(this, this.teardown.bind(this));
  }

  get isLive() {
    return true; // broadcast feed = live feed
  }

  get imageUrl() {
    return this.imageSource?.medium_square?.url
  }

  get url() {
    return this.nowPlayingRecord?.streamUrl;
  }

  get canPlay() {
    return !!this.url
  }

  startTask = task(async () => {
    if (this.settings.debug) {
      console.log(`built broadcast feed for nowPlayingRecord ${this.nowPlayingRecord.id}, starting monitors`);
    }

    try {
      await this.updateAttributesTask.perform(this.nowPlayingRecord) // initial load
      await this.loadCableTask.perform();
    } catch (e) {
      console.error(e)
      //no op
    }
    this.loopTask.perform();
    next(() => this.isLoaded = true)
  });

  loopTask = task(async () => {
    if (macroCondition(isTesting())) {
      return;
    }
    while (true) {
      try {
        if (this.isLoaded && !this.cableConnection?.connected) {
          await timeout(10000);
        } else if (this.isLoaded && this.cableConnection?.connected) {
          await timeout(60000);
        } else {
          await timeout(1000);
          continue;
        }

        // If a token refresh is in flight (e.g. browser resumed from suspension),
        // wait for it to complete before polling with a potentially stale token.
        if (this.settings.loadLaunchRecordTask.isRunning) {
          await this.settings.loadLaunchRecordTask.last;
        }

        await this.loadNowPlayingDataTask.perform();
      } catch (e) {
        //no op
        await timeout(10000);
      }
    }
  });

  get cableConnected() {
    return this.cableConnection?.connected
  }

  loadNowPlayingDataTask = task({ drop: true }, async () => {
    let nowPlaying = await this.store.queryRecord('now-playing', {
        id: this.broadcastId,
        stationId: this.station.uid,
        cache: new Date().getTime(),
        reload: true
      })
      .catch((e) => {
        console.error(e);
      });


    if (nowPlaying) {
      await this.updateAttributesTask.perform(nowPlaying);
      if (nowPlaying.updatedAt != this.nowPlayingUpdatedAt) {
        this.nowPlayingUpdatedAt = nowPlaying.updatedAt
        if (this.itemsFetched) {
          this.loadAiringItemsTask.perform()
        }
      }
    }

    await timeout(1000);
  });

  playTask = task({ restartable: true, maxConcurrency: 1 }, async () => {
    let sound;
    if (!this.loadedSound) {
      try {
        var results = await this.stereo.playTask.linked().perform((this.url), {
          useConnections: ['NativeAudio', 'Howler'],
          metadata: { airing: this.airing },
          xhr: {
            headers: this.session?.authenticatedHeaders,
          },
        });
      } catch (e) {
        throw new Error('failed to load sound', e);
      }
      sound = results?.sound;
    }

    if (this.loadedSound) {
      if (!this.loadedSound.isPlaying && this.loadedSound) {
        await this.loadedSound.togglePause();
      } else {
        // TODO: figure out better queuing
      }
    }

    await timeout(200);
  });

  loadCableTask = task(async () => {
    this.cableConnection = this.socket.subscribe(
      {
        channel: 'NowPlayingChannel',
        station_id: this.station.id,
        broadcast_id: this.broadcastId,
      },
      {
        received: (data) => {
          if (this.isDestroyed) return;
          if (this.settings.debug) {
            console.log(
              '⚡️ received NowPlayingChannel update for stream monitor, handling...'
            );
          }
          this.lastCacheKey = data?.cache;
          this.loadNowPlayingDataTask.perform();
        },
        disconnected: (data) => {
          if (this.isDestroyed) return;
          this.loadNowPlayingDataTask.perform();
        }
      }
    );
  });

  updateAttributesTask = task(async (nowPlaying) => {
    this.station = this.currentNowPlayingModel(nowPlaying, 'station')
    this.broadcast = this.currentNowPlayingModel(nowPlaying, 'broadcast')
    this.airing = this.currentNowPlayingModel(nowPlaying, 'airing')
    this.show = this.currentNowPlayingModel(nowPlaying, 'show')
    this.stream = this.currentNowPlayingModel(nowPlaying, 'stream')
    this.track = this.currentNowPlayingModel(nowPlaying, 'track')
    this.traffic = this.currentNowPlayingModel(nowPlaying, 'traffic')
    this.note = this.currentNowPlayingModel(nowPlaying, 'note')

    let item = _.maxBy(_.compact([this.track, this.traffic, this.note]), (item) => new Date(item.playedAt).getTime())
    this.item = item;
    this.itemType = item?.type;

    try {
      this.feeds.updateAudioMetadata.perform(this.url, {
        station: this.station,
        broadcast:this.broadcast,
        airing: this.airing,
        show: this.show,
        stream: this.stream,
        track: this.track,
        note: this.note,
        traffic: this.traffic,
        imageSource: this.imageSource,
      })
    } catch(e) {
      console.error(`error updating audio metadata`, e)
    }
  });

  currentNowPlayingModel(nowPlayingRecord, modelName) {
    let id = nowPlayingRecord.belongsTo(modelName)?.id()

    if (id) {
      return this.store.peekRecord(modelName, id)
    }
  }

  teardown() {
    this.cableConnection?.unsubscribe()

    console.log('destroyed broadcast feed', this.url)
  }
}
